<template>
  <div class="LanguageSwitcher" :class="{ 'LanguageSwitcher--compact': compact }">
    <button
      ref="declencheur"
      type="button"
      class="LanguageSwitcher__declencheur"
      :class="{ 'LanguageSwitcher__declencheur--ouvert': estOuvert }"
      :aria-label="$t('header.langue.choisir')"
      :aria-expanded="estOuvert"
      aria-haspopup="menu"
      @click.stop="basculer"
    >
      <Icon icon="heroicons-outline:globe-alt" size="20" />
      <span class="LanguageSwitcher__code">{{ locale.toUpperCase() }}</span>
      <!--
        Le chevron est enveloppé : `Icon` déclare `class` comme prop de type chaîne, une liaison
        d'objet ne lui serait donc pas assignable, et la rotation se pose aussi bien sur le parent.
      -->
      <span
        class="LanguageSwitcher__chevron"
        :class="{ 'LanguageSwitcher__chevron--retourne': estOuvert }"
      >
        <Icon icon="heroicons-outline:chevron-down" size="14" />
      </span>
    </button>

    <Transition
      enter-active-class="transition ease-out duration-100"
      enter-from-class="transform opacity-0 scale-95"
      enter-to-class="transform opacity-100 scale-100"
      leave-active-class="transition ease-in duration-75"
      leave-from-class="transform opacity-100 scale-100"
      leave-to-class="transform opacity-0 scale-95"
    >
      <div
        v-if="estOuvert"
        class="LanguageSwitcher__menu"
        role="menu"
        :aria-label="$t('header.langue.choisir')"
      >
        <button
          v-for="langue in LANGUES"
          :key="langue.code"
          type="button"
          role="menuitemradio"
          :aria-checked="locale === langue.code"
          class="LanguageSwitcher__option"
          :class="{ 'LanguageSwitcher__option--active': locale === langue.code }"
          @click="choisir(langue.code)"
        >
          <span class="LanguageSwitcher__badge" aria-hidden="true">{{ langue.code }}</span>
          <span class="LanguageSwitcher__libelle">{{ langue.libelle }}</span>
          <Icon
            v-if="locale === langue.code"
            icon="heroicons-outline:check"
            size="16"
            color="var(--color-main)"
          />
        </button>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import Icon from '@/components/ui/Icon.vue'
import { appliquerLocale, LOCALES_DISPONIBLES, type LocaleDisponible } from '@/plugins/i18n'
import { onUnmounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

withDefaults(
  defineProps<{
    /** Variante du menu latéral mobile : le panneau est étroit et déjà sur fond blanc. */
    compact?: boolean
  }>(),
  { compact: false },
)

/**
 * Chaque langue est nommée dans sa propre langue, et n'est donc pas traduite.
 *
 * C'est la convention des sélecteurs de langue : un visiteur anglophone tombé sur l'interface en
 * français doit reconnaître « English » sans avoir à deviner ce que veut dire « Anglais ».
 */
const LIBELLES: Record<LocaleDisponible, string> = {
  fr: 'Français',
  en: 'English',
}

const LANGUES = LOCALES_DISPONIBLES.map((code) => ({ code, libelle: LIBELLES[code] }))

const { locale } = useI18n()
const estOuvert = ref(false)
const declencheur = ref<HTMLElement | null>(null)

function basculer(): void {
  estOuvert.value = !estOuvert.value
}

function choisir(code: LocaleDisponible): void {
  appliquerLocale(code)
  estOuvert.value = false
  // Le focus revient au déclencheur : sans cela, le bouton d'option disparaît sous le focus et la
  // navigation au clavier repart du début du document.
  declencheur.value?.focus()
}

function fermerAuClicExterieur(evenement: MouseEvent): void {
  const cible = evenement.target as HTMLElement
  if (!cible.closest('.LanguageSwitcher')) estOuvert.value = false
}

function fermerAEchap(evenement: KeyboardEvent): void {
  if (evenement.key !== 'Escape') return

  estOuvert.value = false
  declencheur.value?.focus()
}

// Écouteurs posés à l'ouverture seulement : l'en-tête est monté sur toutes les pages, et deux
// écouteurs globaux permanents pour un menu rarement ouvert se paieraient sur chaque clic.
watch(estOuvert, (ouvert) => {
  if (ouvert) {
    document.addEventListener('click', fermerAuClicExterieur)
    document.addEventListener('keydown', fermerAEchap)
  } else {
    document.removeEventListener('click', fermerAuClicExterieur)
    document.removeEventListener('keydown', fermerAEchap)
  }
})

onUnmounted(() => {
  document.removeEventListener('click', fermerAuClicExterieur)
  document.removeEventListener('keydown', fermerAEchap)
})
</script>

<style lang="scss" scoped>
.LanguageSwitcher {
  position: relative;

  &__declencheur {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    height: 2.5rem;
    padding: 0 0.7rem;
    border: 1px solid rgba(2, 89, 89, 0.18);
    border-radius: 0.75rem;
    background: rgba(2, 89, 89, 0.05);
    color: var(--color-main);
    font-size: 0.8125rem;
    font-weight: 600;
    transition:
      background-color 0.15s ease,
      border-color 0.15s ease,
      color 0.15s ease;

    &:hover {
      background: rgba(2, 89, 89, 0.1);
      border-color: rgba(2, 89, 89, 0.35);
    }

    // Menu ouvert : l'aplat plein reprend le bleu-vert de la charte, comme les boutons d'action
    // voisins de l'en-tête, pour que l'état ouvert se voie sans introduire de couleur nouvelle.
    &--ouvert {
      background: var(--color-main);
      border-color: var(--color-main);
      color: #fff;
    }

    &:focus-visible {
      outline: 2px solid var(--color-main);
      outline-offset: 2px;
    }
  }

  &__code {
    font-variant-numeric: tabular-nums;
    letter-spacing: 0.04em;
  }

  &__chevron {
    display: inline-flex;
    transition: transform 0.15s ease;

    &--retourne {
      transform: rotate(180deg);
    }
  }

  &__menu {
    position: absolute;
    right: 0;
    z-index: 50;
    min-width: 11rem;
    margin-top: 0.5rem;
    padding: 0.25rem;
    border: 1px solid rgba(2, 89, 89, 0.12);
    border-radius: 0.75rem;
    background: rgba(255, 255, 255, 0.97);
    backdrop-filter: blur(8px);
    box-shadow: 0 10px 30px rgba(1, 24, 38, 0.12);
  }

  &__option {
    display: flex;
    width: 100%;
    align-items: center;
    gap: 0.6rem;
    padding: 0.5rem 0.6rem;
    border-radius: 0.5rem;
    text-align: left;
    font-size: 0.8125rem;
    color: #374151;
    transition: background-color 0.12s ease;

    &:hover {
      background: rgba(2, 89, 89, 0.07);
    }

    &--active {
      background: rgba(2, 89, 89, 0.09);
      color: var(--color-main);
      font-weight: 600;
    }

    &:focus-visible {
      outline: 2px solid var(--color-main);
      outline-offset: -2px;
    }
  }

  // Le code de langue tient lieu de drapeau : un drapeau désignerait un pays, alors que le
  // français et l'anglais sont tous deux langues officielles du Cameroun.
  &__badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.6rem;
    height: 1.25rem;
    border-radius: 0.25rem;
    background: rgba(2, 89, 89, 0.12);
    font-size: 0.625rem;
    font-weight: 700;
    text-transform: uppercase;
    color: var(--color-main);
  }

  &__libelle {
    flex: 1 1 auto;
  }

  // Dans le panneau latéral mobile, le menu s'ouvre vers le haut : le sélecteur y siège en pied de
  // panneau, et un menu descendant sortirait de l'écran.
  &--compact {
    .LanguageSwitcher__declencheur {
      width: 100%;
      justify-content: flex-start;
    }

    .LanguageSwitcher__menu {
      right: auto;
      left: 0;
      bottom: calc(100% + 0.5rem);
      margin-top: 0;
      width: 100%;
    }
  }
}

@media (prefers-reduced-motion: reduce) {
  .LanguageSwitcher__declencheur,
  .LanguageSwitcher__chevron,
  .LanguageSwitcher__option {
    transition: none;
  }
}
</style>
